import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ownerApi, inr, monthLabel } from "@/lib/ownerApi";
import ProfitMonthly from "./ProfitMonthly";
import TeacherForecast from "./TeacherForecast";
import Snapshots from "./Snapshots";
import ReceiptCheck from "./ReceiptCheck";

const Card = ({ label, v, tid, tone = "bg-white", sub }) => (
  <div className={`rounded-xl border p-3 ${tone}`} data-testid={tid}>
    <p className="text-[11px] uppercase tracking-wide text-stone-500">{label}</p>
    <p className="text-lg font-bold text-stone-900">{typeof v === "string" ? v : inr(v)}</p>
    {sub && <p className="text-[11px] text-stone-500">{sub}</p>}
  </div>
);

const Block = ({ title, children }) => (
  <section className="space-y-2">
    <h4 className="font-semibold text-stone-800">{title}</h4>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{children}</div>
  </section>
);

const LEVEL = { high: "bg-red-50 border-red-200 text-red-800", medium: "bg-amber-50 border-amber-200 text-amber-800", ok: "bg-emerald-50 border-emerald-200 text-emerald-800" };

export default function ProfitDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try { const r = await ownerApi.get("/fin/dashboard"); setD(r.data); setErr(""); }
    catch (e) { setErr(e.response?.data?.detail || "Could not load"); toast.error(e.response?.data?.detail || "Could not load"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err && !d) return <p className="text-sm text-red-700" data-testid="profit-error">{err}</p>;
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-stone-500" />;
  const { income: i, expenses: e, results: r } = d;
  return (
    <div className="space-y-6" data-testid="profit-dashboard">
      <p className="text-xs text-stone-500">Academic year {d.meta.ay} · June–April · months up to <b>{monthLabel(d.meta.current_month)}</b> are Actual, later months are Expected. Import: {d.meta.import.filename}.</p>
      <Block title="Income">
        <Card label="Historical fees collected (Excel)" v={i.historical_collected_excel} tid="profit-hist-collected" sub={`incl. ${inr(i.undated_historical)} undated lump sums`} />
        <Card label="Live receipts not already in Excel (counted once)" v={i.website_collected_after_import} tid="profit-web-collected" />
        <Card label="Fees earned to date" v={i.earned_to_date} tid="profit-earned" />
        <Card label="Earned but unpaid (outstanding)" v={i.outstanding_earned} tid="profit-outstanding" tone="bg-amber-50" />
        <Card label="Expected future eligible fees" v={i.expected_future} tid="profit-future" tone="bg-sky-50" sub="forecast — not cash" />
        <Card label="Cancelled due to leaving" v={i.cancelled_leaving} tid="profit-cancelled" />
        <Card label="Paid in advance (beyond earned)" v={i.advance_received} tid="profit-advance-received" />
        <Card label="Total estimated annual eligible income" v={i.total_annual_eligible} tid="profit-total-income" tone="bg-emerald-50" />
      </Block>
      <Block title="Expenses">
        <Card label="Actual teacher payments" v={e.teacher_paid} tid="profit-teacher-paid" />
        <Card label="Teacher advances" v={e.teacher_advances} tid="profit-teacher-adv" />
        <Card label="Estimated remaining teacher salary" v={e.remaining_salary} tid="profit-remaining-salary" tone="bg-sky-50" />
        <Card label="Full-year teacher entitlement" v={e.teacher_entitlement_total} tid="profit-entitlement" sub={`incl. ${inr(e.historical_teachers_paid)} paid to Tai/Nikita/Kashish`} />
        {Object.entries(e.by_category).map(([c, v]) => <Card key={c} label={`${c} (actual)`} v={v.actual} tid={`profit-exp-${c}`} sub={`${inr(v.estimate)} still an estimate`} />)}
        <Card label="Past months — estimates not yet verified" v={e.opex_estimate_past_unverified} tid="profit-opex-unverified" tone="bg-amber-50" />
        <Card label="Forecast remaining operating expenses" v={e.opex_forecast_remaining} tid="profit-opex-forecast" tone="bg-sky-50" />
      </Block>
      <Block title="Results">
        <Card label="Cash now (collected − cash paid out)" v={r.cash_now} tid="profit-cash-now" />
        <Card label="Expected remaining collections" v={r.expected_remaining_collections} tid="profit-remaining-coll" tone="bg-sky-50" />
        <Card label="Projected year-end cash" v={r.yearend_cash} tid="profit-yearend-cash" tone="bg-sky-50" />
        <Card label="Estimated full-year operating profit" v={r.operating_profit} tid="profit-operating" tone="bg-emerald-50" sub={`margin ${r.margin}%`} />
      </Block>
      <p className="text-[11px] text-stone-500">Cash is money in hand; profit is income earned minus costs for the whole year. They differ because of prepaid fees, unpaid fees and unpaid salaries.</p>
      <section className="space-y-2" data-testid="profit-risks">
        <h4 className="font-semibold text-stone-800">Forecast risk indicators</h4>
        <div className="grid md:grid-cols-3 gap-2">
          {d.risks.map((x) => <div key={x.label} className={`rounded-lg border p-2 text-sm ${LEVEL[x.level]}`}><b>{x.value}</b> · {x.label}</div>)}
        </div>
      </section>
      <ProfitMonthly rows={d.monthly} />
      <ReceiptCheck r={d.meta.receipts} onChange={load} />
      <TeacherForecast teachers={d.teachers} groups={d.groups} />
      {d.meta.website_not_in_excel?.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="profit-web-review">
          {d.meta.website_not_in_excel.length} website students are not in the Excel and were added before the import, so they are not counted (probably the same children under another spelling): {d.meta.website_not_in_excel.map((s) => `${s.name} (${s.roll_number})`).join(", ")}
        </p>
      )}
      <Snapshots onSaved={load} />
      <section className="text-xs text-stone-600 space-y-1" data-testid="profit-assumptions">
        <h4 className="font-semibold text-stone-800 text-sm">Assumptions</h4>
        <ul className="list-disc pl-5 space-y-0.5">{d.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
      </section>
    </div>
  );
}
